import { ApiError } from '@servicetim/ui/lib';

/**
 * Runs a request and swallows "the endpoint is not there yet" answers (404/405 by default),
 * returning `fallback` instead. Used for the Bolt 7 metadata endpoints while P1 lands them and
 * for the Bolt 8 approvals/notifications endpoints; every other error still propagates.
 */
export async function tolerate<T>(
  run: () => Promise<T>,
  fallback: T,
  statuses: readonly number[] = [404, 405],
): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (error instanceof ApiError && statuses.includes(error.status)) return fallback;
    throw error;
  }
}

/** Marker for adapters: `available` is false when the fallback was used. */
export interface Availability<T> {
  available: boolean;
  data: T;
}

export async function tolerateWithFlag<T>(
  run: () => Promise<T>,
  fallback: T,
  statuses?: readonly number[],
): Promise<Availability<T>> {
  const sentinel = Symbol('fallback');
  const result = await tolerate<T | typeof sentinel>(run, sentinel, statuses);
  return result === sentinel
    ? { available: false, data: fallback }
    : { available: true, data: result };
}
